import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

import { Table, Form, Button, Modal, Row, Col, Nav } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { ADMIN_STUDENT_API } from "../../../api/api";

import { FaEye, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoSearchOutline, IoMailOutline } from "react-icons/io5";
// import { MdBlock } from "react-icons/md";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "react-datepicker/dist/react-datepicker.css";

import excelImg from "../../../assets/excel-icon.png";
import Nofound from "../../../assets/not-found.png";

// import totalViewsImg from "../../../assets/tutor-student-dou-icon1.png";
// import avgCompletionImg from "../../../assets/tutor-student-dou-icon1.png";
// import totalstudentImg from "../../../assets/tutor-student-dou-icon1.png";
// import AvgRatingImg from "../../../assets/tutor-student-dou-icon1.png";

// latestIcons
import totalRegisStudents from "../../../assets/icons/admin/total-registered-students.svg";
import enrolledStudents from "../../../assets/icons/admin/enrolled-students.svg";
import avgFinalAssigScore from "../../../assets/icons/admin/avg-final-assig-score.svg";
import certificateIssued from "../../../assets/icons/student/certificate.svg";

import { initialsFromName } from "../../../utils/getInitials";
import { getPageNumbers } from "../../../utils/pagination";
import { exportToExcel } from "../../../utils/exportExcel";
import { handleApiError } from "../../../utils/apiErrorHandler";
import { useSortableTable } from "../../../hooks/useSortableTable";

import "./AdminStudentManagement.css";

const AdminStudentManagement = () => {
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [studentStats, setStudentStats] = useState({});
  const [selectedStudent, setSelectedStudent] = useState(null);

  // search state
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Filter states
  const [selectedStudentFilter, setSelectedStudentFilter] =
    useState("All Students");
  const [selectedBatchFilter, setSelectedBatchFilter] = useState("All Batches");

  // Modal states
  const [isStudentDetailsModal, setIsStudentDetailsModal] = useState(false);

  const [activeTab, setActiveTab] = useState("courses");

  const [suspendedStudents, setSuspendedStudents] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [studentToSuspend, setStudentToSuspend] = useState(null);

  //loading
  const [loadingStudents, setLoadingStudents] = useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setLoadingStudents(false);
      navigate("/admin-signin");

      return;
    }

    const fetchStudentManagement = async () => {
      try {
        const response = await axios.get(
          ADMIN_STUDENT_API.GET_ADMIN_STUDENT_MANAGEMENT,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          setStudentStats(data.stats || {});
          setStudents(data.data || []);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetching students:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/admin-signin");
      } finally {
        setLoadingStudents(false);
      }
    };

    fetchStudentManagement();
  }, [navigate]);

  const handleViewStudentDetails = async (studentId) => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/admin-signin");

      return;
    }

    try {
      const response = await axios.post(
        ADMIN_STUDENT_API.GET_ADMIN_STUDENT_MANAGEMENT_BY_ID,
        { student_id: studentId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setSelectedStudent({
          profile: data.data.profile,
          overall_progress: data.data.overall_progress,
          courses: data.data.courses,
          assignments: data.data.assignments,
          recent_activity: data.data.recent_activity,
        });

        setIsStudentDetailsModal(true);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetching final assignment:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    }
  };

  const filteredStudentsForDropdown = useMemo(() => {
    return [
      ...new Set(students.map((s) => s.full_name?.trim()).filter(Boolean)),
    ];
  }, [students]);

  const filteredBatchForDropdown = useMemo(() => {
    return [...new Set(students.map((s) => s.batch?.trim()).filter(Boolean))];
  }, [students]);

  // Filter students first
  const filteredStudents = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    return students.filter((s) => {
      const name = s.full_name ?? "";
      const batch = s.batch ?? "";

      return (
        (search === "" ||
          s.student_reg_number?.toString().toLowerCase().includes(searchText) ||
          name.toLowerCase().includes(searchText) ||
          s.email?.toLowerCase().includes(searchText)) &&
        (selectedStudentFilter === "All Students" ||
          name === selectedStudentFilter) &&
        (selectedBatchFilter === "All Batches" || batch === selectedBatchFilter)
      );
    });
  }, [students, search, selectedStudentFilter, selectedBatchFilter]);

  // SORT HOOK (MAIN ADDITION)
  const { sortedData, sortConfig, handleSort } =
    useSortableTable(filteredStudents);

  // Pagination slice
  const studentsPerPage = 5;
  const totalPages = Math.ceil(sortedData.length / studentsPerPage);

  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;

  const currentStudents = sortedData.slice(
    indexOfFirstStudent,
    indexOfLastStudent,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedStudentFilter, selectedBatchFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [totalPages, currentPage]);

  const clearFilters = () => {
    setSearch("");
    setSelectedStudentFilter("All Students");
    setSelectedBatchFilter("All Batches");
    setCurrentPage(1);
  };

  const handleExportStudentDetails = () => {
    const formattedData = sortedData.map((s) => ({
      Register_ID: s.student_reg_number || "N/A",
      Name: s.full_name || "N/A",
      Email: s.email || "N/A",
      Phone: s.phone_number || "N/A",
      Gender: s.gender || "N/A",
      Batch: s.batch || "N/A",
      Enrolled_Courses: s.enrolled_courses || "0",
      Completed_Courses: s.completed_courses || "0",
      Avg_Score: s.average_score || "0",
      Last_Active: s.last_active || "N/A",
    }));

    exportToExcel({
      data: formattedData,
      fileName: "Students",
      sheetName: "Students",
    });
  };

  //  SORT ICON HELPER
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort size={11} />;
    if (sortConfig.direction === "asc") return <FaSortUp size={11} />;
    return <FaSortDown size={11} />;
  };

  // function call
  const pages = getPageNumbers(currentPage, totalPages);

  // student suspend code
  // const handleSuspendClick = (student) => {
  //   setStudentToSuspend(student);
  //   setConfirmModal(true);
  // };

  const confirmSuspendStudent = () => {
    setSuspendedStudents((prev) => {
      if (prev.includes(studentToSuspend.student_id)) {
        return prev.filter((id) => id !== studentToSuspend.student_id);
      } else {
        return [...prev, studentToSuspend.student_id];
      }
    });

    setConfirmModal(false);
    setStudentToSuspend(null);
  };
  const cancelSuspendStudent = () => {
    setConfirmModal(false);
    setStudentToSuspend(null);
  };

  // const activeCount = students.filter((s) => s.status === "Active").length;
  // const inactiveCount = students.filter((s) => s.status === "Inactive").length;
  // const avgProgress = Math.round(
  //   students.reduce((acc, s) => acc + s.progress, 0) / students.length,
  // );

  // export report excel download file
  const handleExportSelectedStudent = (selectedStudent) => {
    if (!selectedStudent) return;

    const { profile, overall_progress, courses } = selectedStudent;

    // Create a string of course titles separated by commas
    const courseList =
      courses.map((c) => c.course_title).join(", ") || "No Courses";

    const data = [
      {
        Name: profile.full_name,
        Email: profile.email,
        Courses: courseList,
        Batch: profile.batch,
        Status: "Active",
        Progress: `${overall_progress}%`,
        Enrolled_Courses: profile.enrolled_courses,
        Completed_Courses: profile.completed_courses,
        Certificates: profile.certificates,
        Avg_Score: `${profile.average_score}%`,
        Last_Active: profile.last_active,
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Student Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(file, `${profile.full_name}_Report.xlsx`);
  };

  // Send email from modal (selectedStudent)
  const handleSendEmailSelectedStudent = (selectedStudent) => {
    if (!selectedStudent?.profile?.email) return;

    const subject = encodeURIComponent("Regarding Your Course Progress");
    const body = encodeURIComponent(
      `Hi ${selectedStudent?.profile?.full_name},\n\nThis is regarding your course progress.\n\nRegards,\nAdmin Team`,
    );

    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&to=${selectedStudent.profile.email}&su=${subject}&body=${body}`,
      "_blank",
    );
  };

  // send mail from the table row
  const handleSendEmail = (student) => {
    if (!student?.email) return;

    const subject = encodeURIComponent("Regarding Your Course Progress");
    const body = encodeURIComponent(
      `Hi ${student.full_name},\n\nThis is regarding your course progress.\n\nRegards,\nAdmin Team`,
    );

    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&to=${student.email}&su=${subject}&body=${body}`,
      "_blank",
    );
  };

  return (
    <div className="admin-student-management-wrapper">
      <h4 className="admin-dashboard-title">Student Management</h4>
      <p className="admin-dashboard-subtitle">
        View student profiles, manage enrollments, and track learning progress.
      </p>

      <Row className="admin-student-section-cards-row">
        <Col lg={3} md={4} sm={6} xs={12} className="mb-3">
          <div className="admin-student-section-stat-card">
            <h6 className="admin-student-section-stat-title">
              Total Registered Students
            </h6>
            <img
              src={totalRegisStudents}
              className="admin-student-stat-icon"
              alt="students"
            />

            <h3 className="admin-student-section-stat-value">
              {Number(studentStats.total_students) || "-"}
            </h3>
            <span className="admin-student-section-stat-sub">
              +12% from last month
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-student-section-stat-card">
            <h6 className="admin-student-section-stat-title">
              Enrolled Students
            </h6>
            <img
              src={enrolledStudents}
              className="admin-student-stat-icon"
              alt="learners"
            />

            <h3 className="admin-student-section-stat-value">
              {Number(studentStats.active_students) || "-"}
            </h3>
            <span className="admin-student-section-stat-sub">
              +3% vs last month
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-student-section-stat-card">
            <h6 className="admin-student-section-stat-title">
              Avg Final Assignments Score
            </h6>
            <img
              src={avgFinalAssigScore}
              className="admin-student-stat-icon"
              alt="progress"
            />

            <h3 className="admin-student-section-stat-value">
              {Number(studentStats.avg_progress) || "-"}
            </h3>
            <span className="admin-student-section-stat-sub">
              +8% w/o churn
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-student-section-stat-card">
            <h6 className="admin-student-section-stat-title">
              Certificates Issued
            </h6>
            <img
              src={certificateIssued}
              className="admin-student-stat-icon"
              alt="certificate"
            />

            <h3 className="admin-student-section-stat-value">
              {Number(studentStats.certificates_issued) || "-"}
            </h3>
            <span className="admin-student-section-stat-sub">
              Based on 3,400 reviews
            </span>
          </div>
        </Col>
      </Row>

      <div className="admin-student-management-filters">
        <Row className="mb-3 admin-student-management-filters-rowsss">
          <Col md={4}>
            <div className="admin-student-management-search-box">
              <span className="admin-student-management-search-icon">
                <IoSearchOutline />
              </span>
              <Form.Control
                type="text"
                placeholder="Search by enroll id, name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="admin-student-management-search-input"
              />
            </div>
          </Col>

          <Col md={3}>
            <Form.Select
              value={selectedStudentFilter}
              onChange={(e) => setSelectedStudentFilter(e.target.value)}
            >
              <option value="All Students">All Students</option>
              {filteredStudentsForDropdown.map((student, index) => (
                <option key={index} value={student}>
                  {student}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Select
              value={selectedBatchFilter}
              onChange={(e) => setSelectedBatchFilter(e.target.value)}
            >
              <option value="All Batches">All Batches</option>
              {filteredBatchForDropdown.map((batch, index) => (
                <option key={index} value={batch}>
                  {batch}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={1}>
            <Button
              className="admin-student-management-clear-btn"
              onClick={clearFilters}
            >
              Clear
            </Button>
          </Col>

          <Col md={1}>
            <Button
              size="sm"
              className="admin-student-management-table-export"
              onClick={handleExportStudentDetails}
              disabled={sortedData.length === 0}
              title={
                sortedData.length === 0
                  ? "No data available to export"
                  : "Export to Excel"
              }
              style={{
                cursor: sortedData.length === 0 ? "not-allowed" : "pointer",
                opacity: sortedData.length === 0 ? 0.5 : 1,
              }}
            >
              <img
                src={excelImg}
                alt="Excel"
                className="admin-student-management-table-export-excel-img"
              />
            </Button>
          </Col>
        </Row>

        {/* <div className="admin-student-management-summary border-top">
          <span className="admin-student-management-summary-active">
            {activeCount} Active
          </span>

          <span className="admin-student-management-summary-inactive">
            {inactiveCount} Inactive
          </span>

          <span className="admin-student-management-summary-progress">
            Avg Progress: <strong>{avgProgress}%</strong>
          </span>
        </div> */}
      </div>

      {/* TABLE  */}
      <div className="admin-student-management-table-wrapper">
        <Table className="admin-student-management-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("student_reg_number")}
                style={{ cursor: "pointer" }}
              >
                Enroll Id {renderSortIcon("student_reg_number")}
              </th>
              <th
                onClick={() => handleSort("full_name")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Student {renderSortIcon("full_name")}{" "}
                </div>
              </th>
              <th
                onClick={() => handleSort("email")}
                style={{ cursor: "pointer" }}
              >
                Email {renderSortIcon("email")}
              </th>
              <th>Phone</th>
              <th
                onClick={() => handleSort("gender")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Gender {renderSortIcon("gender")}{" "}
                </div>
              </th>
              <th>Batch</th>
              {/* <th>Status</th> */}
              <th
                onClick={() => handleSort("enrolled_courses")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Enrolled {renderSortIcon("enrolled_courses")}
                </div>
              </th>
              <th
                onClick={() => handleSort("completed_courses")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Completed {renderSortIcon("completed_courses")}
                </div>
              </th>
              <th>AvgScore</th>
              <th>
                <div className="admin-dashboard-sort-header">Last Active </div>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingStudents ? (
              <tr>
                <td colSpan="12" style={{ textAlign: "center" }}>
                  Loading students...
                </td>
              </tr>
            ) : currentStudents.length > 0 ? (
              currentStudents.map((s) => (
                <tr key={s.student_id}>
                  <td title={s.student_reg_number}>
                    {s.student_reg_number || "-"}
                  </td>
                  <td title={s.full_name}>{s.full_name || "-"}</td>
                  <td title={s.email}>{s.email || "-"}</td>
                  <td title={s.phone_number}>{s.phone_number || "-"}</td>
                  <td title={s.gender}>{s.gender || "-"}</td>
                  <td title={s.batch}>{s.batch || "-"}</td>
                  {/* <td
                  className={`admin-student-management-status ${s.status.toLowerCase()}`}
                  >
                    {s.status}
                    Active
                  </td> */}
                  <td title={s.enrolled_courses}>
                    {Number(s.enrolled_courses) || "-"}
                  </td>
                  <td title={s.completed_courses}>
                    {Number(s.completed_courses) || "-"}
                  </td>
                  <td
                    title={s.average_score}
                    className={`admin-student-management-avgscore ${
                      s.average_score < 75 ? "low-score" : "high-score"
                    }`}
                  >
                    {/* {s.average_score}% */}

                    {Number(s.average_score) > 0 ? `${s.average_score}%` : "-"}
                  </td>
                  <td title={s.last_active}>{s.last_active || "-"}</td>
                  <td>
                    <div className="admin-student-management-action-icons">
                      <FaEye
                        title={s.batch ? "View Details" : "Batch not assigned"}
                        className={`admin-student-management-eye ${!s.batch ? "disabled-action-admin-dashbaord" : ""}`}
                        onClick={() => {
                          if (!s.batch) return;
                          handleViewStudentDetails(s.student_id);
                        }}
                      />

                      {/* <MdBlock
                        title={
                          suspendedStudents.includes(s.student_id)
                            ? "Unsuspend Student"
                            : "Suspend Student"
                        }
                        className={`admin-student-management-block ${
                          suspendedStudents.includes(s.student_id)
                            ? "suspended"
                            : ""
                        }`}
                        onClick={() => handleSuspendClick(s)}
                      /> */}
                      <IoMailOutline
                        title="Send Mail"
                        className="admin-student-management-mail"
                        onClick={() => handleSendEmail(s)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" style={{ textAlign: "center" }}>
                  <img
                    src={Nofound}
                    alt="No Data Found"
                    className="admin-all-tutor-empty-img"
                  />
                  <p className="admin-dashboard-no-data">No Data Found</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="tutor-assignment-table-pagination">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          {pages.map((page, idx) =>
            page === "..." ? (
              <span key={idx} style={{ padding: "0 3px" }}>
                ...
              </span>
            ) : (
              <Button
                key={idx}
                active={currentPage === page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </Button>
            ),
          )}

          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      <Modal
        show={isStudentDetailsModal}
        onHide={() => setIsStudentDetailsModal(false)}
        backdrop="static"
        keyboard={false}
        size="lg"
        className="admin-student-management-model-bodyssss"
      >
        <Modal.Header
          closeButton
          className="admin-student-management-model-headers"
        >
          <Modal.Title className="admin-student-management-model-header-title">
            Student Details
            <p className="admin-student-management-subtitle">
              View the student profile, enrolled courses, and learning
              activity.
            </p>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="admin-student-management-modal-body">
          {/* STUDENT INFO */}
          <div className="admin-student-management-student-card">
            <div className="admin-student-management-avatar">
              {initialsFromName(selectedStudent?.profile?.full_name)}
            </div>
            <div className="admin-student-management-avatar-name">
              <h6>{selectedStudent?.profile?.full_name || "-"}</h6>
              <p>{selectedStudent?.profile?.email || "-"}</p>
            </div>
          </div>

          {/* STATS */}
          <div className="admin-student-management-stats">
            <div className="admin-student-management-stat-box">
              <span>Enrolled Courses</span>
              <strong>
                {selectedStudent?.profile?.enrolled_courses || "-"}
              </strong>
            </div>
            <div className="admin-student-management-stat-box">
              <span>Completed Courses</span>
              <strong>
                {selectedStudent?.profile?.completed_courses || "-"}
              </strong>
            </div>
            <div className="admin-student-management-stat-box">
              <span>Certificates</span>
              <strong>{selectedStudent?.profile?.certificates || "-"}</strong>
            </div>
            <div className="admin-student-management-stat-box">
              <span>Avg Final Assignment Score</span>
              <strong>{selectedStudent?.profile?.average_score}%</strong>
            </div>
          </div>

          {/* OVERALL PROGRESS */}
          <div className="admin-student-management-overall-progress">
            <div className="admin-student-management-progress-header">
              <span>Overall Progress</span>
              <strong>{selectedStudent?.overall_progress || 0}%</strong>
            </div>

            <div className="admin-student-management-progress-bar">
              <div
                className="admin-student-management-progress-fill"
                style={{ width: `${selectedStudent?.overall_progress || 0}%` }}
              ></div>
            </div>

            {/* <small>
              {selectedStudent?.completed || 0} of{" "}
              {selectedStudent?.enrolled || 0} courses completed
            </small> */}
          </div>

          {/* TABS */}
          <Nav
            variant="tabs"
            className="admin-pending-course-tabs admin-student-management-tabs mb-3"
          >
            {["courses", "assignments", "activity"].map((tab) => (
              <Nav.Item key={tab}>
                <Nav.Link
                  active={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          {/* TAB CONTENT */}
          {activeTab === "courses" &&
            (selectedStudent?.courses?.length > 0 ? (
              <div className="admin-student-management-course-list">
                {selectedStudent.courses.map((course) => (
                  <div
                    key={course.course_id}
                    className="admin-student-management-course-item"
                  >
                    <div>
                      <h6>{course.course_title}</h6>
                      <span>{course.category_name}</span>
                    </div>
                    <strong>{course.progress_percentage}%</strong>
                  </div>
                ))}
              </div>
            ) : (
              <p className="admin-dashboard-no-data">
                No course data available
              </p>
            ))}

          {activeTab === "assignments" &&
            (selectedStudent?.assignments?.length > 0 ? (
              <div className="admin-student-management-course-list">
                {selectedStudent.assignments.map((assg, index) => (
                  <div
                    key={index}
                    className={`admin-student-management-course-item ${assg.status.toLowerCase()}`}
                  >
                    <div>
                      <h6>{assg.assignment_title}</h6>
                      <span>{assg.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="admin-dashboard-no-data">
                No assignment data available
              </p>
            ))}

          {activeTab === "activity" &&
            (selectedStudent?.recent_activity?.length > 0 ? (
              <div className="admin-student-management-activity-list">
                {selectedStudent.recent_activity.map((act, idx) => (
                  <div
                    key={idx}
                    className="admin-student-management-activity-item"
                  >
                    <div>
                      <h6>{act.course_title}</h6>
                      <span>{act.category_name}</span>
                      {act.video_title && (
                        <p className="admin-student-management-video-title">
                          {act.video_title}
                        </p>
                      )}
                    </div>
                    <small>{act.activity_time}</small>
                  </div>
                ))}
              </div>
            ) : (
              <p className="admin-dashboard-no-data">
                No activity data available
              </p>
            ))}

          {/* ACTION BUTTONS */}
          <div className="admin-student-management-actions">
            <Button
              className="admin-student-management-primary-btn-send btn-primary"
              onClick={() => handleSendEmailSelectedStudent(selectedStudent)}
            >
              Send Email
            </Button>
            <Button
              className="admin-student-management-second-btn-cancel"
              onClick={() => handleExportSelectedStudent(selectedStudent)}
            >
              Export Report
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={confirmModal}
        onHide={cancelSuspendStudent}
        centered
        className="admin-student-management-third-model"
        backdrop="static"
      >
        <Modal.Header
          closeButton
          className="admin-student-management-third-model-header"
        >
          <Modal.Title className="admin-student-management-third-model-title">
            {suspendedStudents.includes(studentToSuspend?.student_id)
              ? "Confirm Unsuspend"
              : "Confirm Suspension"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="admin-student-management-third-model-body">
          <p className="admin-student-management-third-model-text">
            Are you sure you want to{" "}
            <span className="admin-student-management-third-model-action">
              {suspendedStudents.includes(studentToSuspend?.student_id)
                ? "unsuspend"
                : "suspend"}
            </span>{" "}
            <span className="admin-student-management-third-model-name">
              {studentToSuspend?.full_name}
            </span>
            ?
          </p>
        </Modal.Body>

        <Modal.Footer className="admin-student-management-third-model-footer">
          <Button
            className="admin-student-management-third-model-btn-cancel"
            onClick={cancelSuspendStudent}
          >
            No
          </Button>

          <Button
            className={`admin-student-management-third-model-btn-confirm ${
              suspendedStudents.includes(studentToSuspend?.student_id)
                ? "unsuspend"
                : "suspend"
            }`}
            onClick={confirmSuspendStudent}
          >
            Yes,{" "}
            {suspendedStudents.includes(studentToSuspend?.student_id)
              ? "Unsuspend"
              : "Suspend"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminStudentManagement;
